import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import api, { formatPrice, mediaUrl, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => { setLoading(true); api.get("/admin/products").then(({ data }) => setProducts(data.products)).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
        try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); load(); }
        catch (err) { toast.error(apiError(err)); }
    };

    const toggleActive = async (p) => {
        try {
            await api.put(`/admin/products/${p.id}`, { ...p, active: !p.active });
            toast.success(p.active ? "Product deactivated" : "Product activated");
            load();
        } catch (err) { toast.error(apiError(err)); }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold">Products</h1>
                <Link to="/admin/products/new" data-testid="admin-add-product" className="bg-ink text-white px-4 py-2.5 rounded text-sm flex items-center gap-2 hover:bg-gray-800"><Plus className="h-4 w-4" /> Add Product</Link>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                            <tr><th className="text-left p-4">Product</th><th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? <tr><td colSpan={5} className="p-8 text-center text-gray-400">Loading…</td></tr> :
                            products.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-gray-400">No products yet.</td></tr> :
                            products.map((p) => (
                                <tr key={p.id} data-testid={`admin-product-${p.slug}`}>
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 bg-cream-soft rounded overflow-hidden shrink-0">{p.images?.[0] && <img src={mediaUrl(p.images[0].url)} alt="" className="w-full h-full object-cover" />}</div>
                                            <div><p className="font-medium">{p.name}</p><p className="text-xs text-gray-400">{p.category}</p></div>
                                        </div>
                                    </td>
                                    <td className="p-4">{formatPrice(p.price)}</td>
                                    <td className="p-4"><span className={p.stock <= 5 ? "text-red-500 font-medium" : ""}>{p.stock}</span></td>
                                    <td className="p-4"><span className={`text-xs px-2 py-1 rounded ${p.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                                    <td className="p-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => toggleActive(p)} title="Toggle active" className="p-2 hover:bg-gray-100 rounded">{p.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                                            <Link to={`/admin/products/${p.id}`} data-testid={`edit-product-${p.slug}`} className="p-2 hover:bg-gray-100 rounded"><Pencil className="h-4 w-4" /></Link>
                                            <button onClick={() => remove(p)} data-testid={`delete-product-${p.slug}`} className="p-2 hover:bg-gray-100 rounded text-red-500"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
